import { useCallback, useState } from "react";

const STORAGE_KEY = "list-create-favorites";

const readFavorites = (): Set<string> => {
	try {
		const stored = localStorage.getItem(STORAGE_KEY);

		if (!stored) {
			return new Set();
		}

		const parsed = JSON.parse(stored);

		return Array.isArray(parsed)
			? new Set(parsed.filter((id): id is string => typeof id === "string"))
			: new Set();
	} catch {
		return new Set();
	}
};

const persistFavorites = (favorites: Set<string>): void => {
	try {
		localStorage.setItem(STORAGE_KEY, JSON.stringify([...favorites]));
	} catch {
		// Ignore write failures (e.g. storage disabled / quota exceeded).
	}
};

export interface UseCatalogFavorites {
	favorites: Set<string>;
	isFavorite: (id: string) => boolean;
	toggleFavorite: (id: string) => void;
}

export const useCatalogFavorites = (): UseCatalogFavorites => {
	const [favorites, setFavorites] = useState<Set<string>>(readFavorites);

	const toggleFavorite = useCallback((id: string) => {
		setFavorites((current) => {
			const next = new Set(current);

			if (next.has(id)) {
				next.delete(id);
			} else {
				next.add(id);
			}

			persistFavorites(next);

			return next;
		});
	}, []);

	const isFavorite = useCallback((id: string) => favorites.has(id), [favorites]);

	return { favorites, isFavorite, toggleFavorite };
};
